import { createReducer, on, createFeature } from '@ngrx/store'
import {
  createAssignedTo,
  createAssignedToFailure,
  createAssignedToSuccess,
  createRole,
  createRoleFailure,
  createRoleSuccess,
  deleteAssignedTo,
  deleteAssignedToFailure,
  deleteAssignedToSuccess,
  deleteRole,
  deleteRoleFailure,
  deleteRoleSuccess,
  loadAssignedTo,
  loadAssignedToFailure,
  loadAssignedToSuccess,
  loadRoleByIdFailure,
  loadRoleByIdSuccess,
  loadRoles,
  loadRolesFailure,
  loadRolesSuccess,
  loadUnassignedUsersSuccess,
  resetRoleState,
  updateRole,
  updateRoleFailure,
  updateRoleSuccess,
  upsertValidUpto,
  upsertValidUptoFailure,
  upsertValidUptoSuccess
} from './role.actions'
import { RoleState } from './role.state'

export const initialState: RoleState = {
  roles: [],
  loading: false,
  error: null,
  totalCount: 0,
  assignedTo: null,
  role: null,
  unassignedUsers: null
}

export const roleReducer = createReducer(
  initialState,
  on(loadRoles, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(loadRolesSuccess, (state, { roles, totalCount }) => ({
    ...state,
    roles: roles,
    loading: false,
    error: null,
    totalCount
  })),
  on(loadRolesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(createRole, state => ({ ...state, loading: true })),
  on(createRoleSuccess, (state, { role }) => ({
    ...state,
    roles: [...state.roles, role],
    loading: false
  })),
  on(createRoleFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(updateRole, state => ({ ...state, loading: true })),
  on(updateRoleSuccess, (state, { role }) => ({
    ...state,
    role,
    loading: false
  })),
  on(updateRoleFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(loadRoleByIdSuccess, (state, { role }) => {
    // console.log('role', role)
    return {
      ...state,
      role: role,
      error: null
    }
  }),
  on(loadRoleByIdFailure, (state, { error }) => ({
    ...state,
    role: null,
    error
  })),
  on(loadAssignedTo, upsertValidUpto, deleteAssignedTo, createAssignedTo, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    loadAssignedToSuccess,
    upsertValidUptoSuccess,
    deleteAssignedToSuccess,
    createAssignedToSuccess,
    (state, { assignedTo }) => ({
      ...state,
      loading: false,
      error: null,
      assignedTo
    })
  ),
  on(
    loadAssignedToFailure,
    upsertValidUptoFailure,
    deleteAssignedToFailure,
    createAssignedToFailure,
    (state, { error }) => ({
      ...state,
      loading: false,
      error
    })
  ),
  on(deleteRole, (state, { id }) => ({
    ...state,
    roles: state.roles.map(role => (String(role.id) === id ? { ...role, disabled: true } : role))
  })),
  on(deleteRoleSuccess, (state, { id }) => {
    // Update the business state to reflect that it is successfully deleted
    const updatedState = {
      ...state,
      roles: state.roles.filter(b => b.id !== id), // Remove the business from the list
      error: null
    }
    return updatedState
  }),
  on(deleteRoleFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(loadUnassignedUsersSuccess, (state, { users }) => ({
    ...state,
    unassignedUsers: users
  })),
  on(resetRoleState, () => initialState)
)

// Create the feature for the business state
export const roleFeature = createFeature({
  name: 'role', // Feature name
  reducer: roleReducer
})
export type { RoleState }
// Export the feature's selectors
export const {
  name,
  reducer,
  selectRoles,
  selectLoading,
  selectError,
  selectRole,
  selectAssignedTo,
  selectTotalCount
} = roleFeature
